import React, { createContext, useContext, useState } from 'react';

import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';

import { sumFormFormatter, sumFormFormatterWithNegative } from '@shared/helpers/sumFormFormatter';
import { CustomModal } from '@shared/ui';

import { AccountAddBody } from './AccountAddBody';
import { AccountAddFooter } from './AccountAddFooter';
import { Context } from '../../Accounts';
import { fetchAddAccount, fetchIntegrationAccountListByCompany } from '../../accuntApi';

const accountTypeMapping = {
    notBind: 'simple',
    company: 'legal',
    employee: 'personal',
};

export const ModalAddContext = createContext({});
export const ModalAdd = ({ onShowModal }) => {
    const {
        integrationList,
        accounts,
        setAccounts,
        employeelList,
        legalList,
        currentList,
        modalState,
    } = useContext(Context);

    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState('company');
    const [isActiveCheckbox, setIsActiveCheckbox] = useState(false);
    const [isActiveSwitcher, setIsActiveSwitcher] = useState(!!modalState.currentAccount);
    const [errors, setErrors] = useState({
        company: null,
        employee: null,
        notBind: null,
    });

    const initForm = {
        account_number: modalState.currentAccount ? modalState.currentAccount.account_number : '',
        currency: modalState.currentAccount ? modalState.currentAccount.currency.id : null,
        opening_balance_date: new Date(),
        opening_balance_value: modalState.currentAccount
            ? modalState.currentAccount.opening_balance_value
            : '0',
        title: modalState.currentAccount ? modalState.currentAccount.title : '',
        user_bind: modalState.currentAccount ? modalState.currentAccount.user_bind : null,
        employee: null,
        legal: modalState.currentAccount ? modalState.currentAccount.user_bind.guid : null,
        integration: null,
        integrationAccount: null,
    };

    const [integrationAccountList, setIntegrationAccountList] = useState({
        list: [],
        isLoading: false,
        datetime: '',
    });

    const [formState, setFormState] = useState(initForm);

    const formHandler = (event) => {
        const { name, value, type } = event.target;
        setFormState((prevState) => ({ ...prevState, [name]: value }));
    };

    const sumFormHandler = (event) => {
        const { name, value } = event.target;

        let formattedValue = sumFormFormatter(value);
        if (name === 'opening_balance_value') {
            formattedValue = sumFormFormatterWithNegative(value);
        }

        setFormState((prevState) => ({
            ...prevState,
            [name]: formattedValue,
        }));
    };
    const onCancelButton = () => {
        onShowModal();
        setFormState(initForm);
    };

    const onCurrencyChange = (value) => {
        setFormState((prevState) => ({ ...prevState, currency: value }));
    };

    const onIntegrationChange = (value) => {
        setIntegrationAccountList((prevState) => ({ ...prevState, isLoading: true }));
        setFormState((prevState) => ({ ...prevState, integration: value }));

        const url = helpable.replaceUrlParameters(
            routable.getRoute('getIntegrationAccountListByCompany'),
            {
                _integrationType_: value,
            }
        );

        const callback = (data) => {
            if (data.status === 'success') {
                const getIntegrationAccounts = data.data.map((account) => ({
                    shortNumber: account.account_number
                        ? account.short_account_number
                        : t('ModalAdd.return.callback.shortNumber.notNumber'),
                    startBalance: account.start_balance,
                    sign: '₽',
                    integrationAccounts: account,
                    value: account.account_number,
                }));
                setIntegrationAccountList((prevState) => ({
                    isLoading: false,
                    list: getIntegrationAccounts,
                    datetime: data.datetime,
                }));
            } else {
                setIntegrationAccountList((prevState) => ({
                    isLoading: false,
                }));
            }
        };

        const errorHandler = () => {
            setIntegrationAccountList((prevState) => ({
                list: [],
                isLoading: false,
                datetime: '',
            }));
        };

        fetchIntegrationAccountListByCompany(url, callback, errorHandler);
    };
    const onEmployeeChange = (value) => {
        const employeeValue = employeelList.find((item) => item.value === value);

        setFormState((prevState) => ({
            ...prevState,
            user_bind: {
                guid: employeeValue.employee.guid,
                name: employeeValue.employee.display_name,
                type: 'personal',
                email: employeeValue.value,
            },
        }));

        setFormState((prevState) => ({ ...prevState, employee: value }));
    };

    const onLegalChange = (value) => {
        const legalValue = legalList.find((item) => item.value === value);

        setFormState((prevState) => ({
            ...prevState,
            user_bind: { guid: legalValue.value, name: legalValue.title, type: 'legal' },
        }));
        setFormState((prevState) => ({ ...prevState, legal: value }));
    };

    const onIntegrationAccountChange = (value) => {
        const currentAccount = integrationAccountList.list.find(
            (accuont) => accuont.value === value
        );

        setFormState((prevState) => ({
            ...prevState,
            integrationAccount: value,
            title: currentAccount.integrationAccounts.account_name,
            account_number: currentAccount.integrationAccounts.account_number,
            opening_balance_value: currentAccount.startBalance,
        }));
    };

    const onSubmitButton = () => {
        loader.show();
        const url = routable.getRoute('addAccount');
        const legal = activeTab === 'company' ? formState.legal : legalList[0].value;
        const employee = activeTab === 'employee' ? formState.employee : employeelList[0].value;
        const formData = new FormData();
        const date = moment(formState.opening_balance_date).format('DD.MM.YYYY');
        formData.append('accountType', accountTypeMapping[activeTab]);
        formData.append('legalEntity', legal || '0');
        formData.append('employee', employee || '0');
        formData.append('title', formState.title);
        formData.append('accountNumber', formState.account_number);
        formData.append('accountStartBalance', formState.opening_balance_value);
        formData.append('currency', formState.currency ? formState.currency : '0');
        if (isActiveSwitcher) {
            formData.append('integration', formState.integration ? formState.integration : '');
            formData.append('accountStartDate', integrationAccountList.datetime);
        } else {
            formData.append('accountStartDate', date);
        }

        if (isActiveCheckbox) {
            formData.append('isDefault', '1');
        }
        formData.append('submit', '1');
        const callback = (data) => {
            setErrors({
                company: null,
                employee: null,
                notBind: null,
            });
            const currentCurrency = currentList.find(
                (option) => option.value == formState.currency
            );

            const newAccount = {
                guid: data.data.guid,
                title: data.data.title,
                account_number: data.data.account_number,
                user_bind: formState.user_bind,
                current_balance: data.data.current_balance,
                currency: currentCurrency.currency,
                default_account: data.data.is_default,
                is_archive: false,
                opening_balance_date: moment(formState.opening_balance_date).format('YYYY.MM.DD'),
                opening_balance_value: data.data.current_balance,
                avalible_delete: true,
                integration: data.data.integration,
            };

            if (isActiveCheckbox) {
                const newAccounts = [...accounts];

                for (let i = 0; i < newAccounts.length; i++) {
                    if (newAccounts[i].default_account) {
                        const newEditedAccount = { ...newAccounts[i], default_account: false };

                        newAccounts[i] = newEditedAccount;
                    }
                }

                setAccounts([...newAccounts, newAccount]);
            } else {
                setAccounts([...accounts, newAccount]);
            }
            onShowModal();
            if (isActiveSwitcher) {
                swal({
                    text: t('ModalAdd.return.callback.isActiveSwitcher.swal.text'),
                    cancel: false,
                    confirm: true,
                    button: {
                        text: t('ModalAdd.return.callback.isActiveSwitcher.swal.button'),
                        value: true,
                        visible: true,
                        className: 'btn btn-primary',
                    },
                });
            }
        };
        const errorHandler = (data) => {
            setErrors((prevState) => ({ ...prevState, [activeTab]: data.errors }));
        };
        fetchAddAccount(url, callback, formData, errorHandler);
    };

    const onSwitcherChange = () => {
        setIsActiveSwitcher((prevState) => !prevState);
    };
    const onCheckboxChange = () => {
        setIsActiveCheckbox((prevState) => !prevState);
    };

    const onDateChange = (newDate) => {
        setFormState((prevState) => ({ ...prevState, opening_balance_date: new Date(newDate) }));
    };

    const selectedCurrency = currentList.find((item) => item.value == formState.currency);
    const selectedLegal = legalList.find((item) => item.value == formState.legal);
    const selectedEmployee = employeelList.find((item) => item.value == formState.employee);
    const selectedIntegration = integrationList.find((item) => item.value == formState.integration);
    const selectedIntegrationAccount = integrationAccountList.list.find(
        (item) => item.value == formState.integrationAccount
    );

    const modalAddState = {
        activeTab,
        setActiveTab,
        isActiveCheckbox,
        setIsActiveCheckbox,
        isActiveSwitcher,
        setIsActiveSwitcher,
        currentList,
        onShowModal,
        onCancelButton,
        onSubmitButton,
        onSwitcherChange,
        onCheckboxChange,
        formState,
        setFormState,
        formHandler,
        onCurrencyChange,
        selectedCurrency,
        legalList,
        employeelList,
        selectedLegal,
        selectedEmployee,
        onLegalChange,
        onEmployeeChange,
        onDateChange,
        setErrors,
        errors,
        onIntegrationChange,
        selectedIntegration,
        integrationAccountList,
        selectedIntegrationAccount,
        onIntegrationAccountChange,
        sumFormHandler,
    };

    return (
        <ModalAddContext.Provider value={modalAddState}>
            <CustomModal.Header>{t('ModalAdd.return.ModalHeader.text')}</CustomModal.Header>
            <AccountAddBody />
            <CustomModal.Footer>
                <AccountAddFooter />
            </CustomModal.Footer>
        </ModalAddContext.Provider>
    );
};
